// src/features/calendar/EntitySchedulePanel.tsx
// "Schedule" card for the right rail of another module's entity (task,
// document, …). A person scheduling a task is looking at the task — so the
// calendar orchestration RPCs live here, not under /calendar.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useCalendarCalendar, useCalendarCalendarPolicy, useI18n } from '@react/hooks';
import { Button, Alert } from '@react/components';
import { useScope } from '@/hooks/useScope';
import { ListEntitySchedule } from '@core/api/endpoints/calendar/list_entity_schedule';
import type { Entity } from '@core/types/entity';
import type { CalendarAspect, CalendarPolicy, CheckEntityAvailabilityResponse } from '@core/types/calendar';
import type { PaginatedRequest } from '@core/types/pagination';
import {
  addDays,
  dateTimeToIso,
  errMessage,
  fmtDateTime,
  fmtTime,
  makeEntity,
  toDateInputValue,
  toTimeInputValue,
} from './time';

interface EntitySchedulePanelProps {
  /** The host entity being scheduled (task, document, …). */
  entity: Entity;
  /** CalendarEventTypeDefinition slug; defaults to the entity's type name. */
  eventTypeId?: string;
}

export const EntitySchedulePanel = observer(({ entity, eventTypeId }: EntitySchedulePanelProps) => {
  const { t, locale } = useI18n();
  const scope = useScope();
  const store = useCalendarCalendar();
  const policyStore = useCalendarCalendarPolicy();

  const typeId = eventTypeId || entity.type_name;

  const [aspect, setAspect] = useState<CalendarAspect | null>(null);
  const [policy, setPolicy] = useState<CalendarPolicy | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [dateStart, setDateStart] = useState(toDateInputValue(new Date()));
  const [timeStart, setTimeStart] = useState('09:00');
  const [durationMin, setDurationMin] = useState<number | ''>(60);
  const [reason, setReason] = useState('');
  const [check, setCheck] = useState<CheckEntityAvailabilityResponse | null>(null);

  const [unavailable, setUnavailable] = useState(false);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [scheduleRes, contextRes] = await Promise.all([
        ListEntitySchedule(typeId, {
          time_min: addDays(new Date(), -30).toISOString(),
          time_max: addDays(new Date(), 365).toISOString(),
          pagination: { cursor: '', limit: 100, direction: 'forward' },
        } as unknown as PaginatedRequest),
        policyStore
          .ResolveCalendarContext({ scope_entity: entity, scope_chain: [entity] })
          .catch(() => null),
      ]);
      const mine = (scheduleRes.entries ?? []).find((e) => e.entity?.id === entity.id);
      setAspect(mine?.calendar ?? null);
      setPolicy(contextRes?.effective_policy ?? null);
      setUnavailable(false);
    } catch {
      // The calendar service has no event type registered for this entity
      // (or is unreachable). That is a deployment state, not a user mistake —
      // degrade to a quiet note instead of a red alert with a dead form.
      setUnavailable(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, [entity.id]);

  const isScheduled = !!aspect?.start_time && aspect.status !== 'EVENT_STATUS_CANCELLED';

  const openForm = () => {
    if (aspect?.start_time) {
      const s = new Date(aspect.start_time);
      setDateStart(toDateInputValue(s));
      setTimeStart(toTimeInputValue(s));
      if (aspect.end_time) {
        setDurationMin(Math.max(15, Math.round((new Date(aspect.end_time).getTime() - s.getTime()) / 60000)));
      }
    }
    setCheck(null);
    setReason('');
    setFormOpen(true);
  };

  const startEnd = () => {
    const start = dateTimeToIso(dateStart, timeStart);
    const mins = durationMin === '' ? 60 : durationMin;
    const end = new Date(new Date(start).getTime() + mins * 60000).toISOString();
    return { start, end };
  };

  const checkAvailability = async () => {
    setBusy(true);
    setError(null);
    try {
      const { start, end } = startEnd();
      const res = await store.CheckEntityAvailability(typeId, {
        scope: makeEntity(scope.productId, 'product', scope),
        event_type_id: typeId,
        start_time: start,
        end_time: end,
        participants: [],
        exclude_entity: isScheduled ? entity : undefined,
      });
      setCheck(res);
    } catch (e) {
      setError(errMessage(e, t('calendar.entitySchedule.checkError')));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const { start, end } = startEnd();
      if (isScheduled) {
        await store.RescheduleEntity(entity.type_name, entity.id, {
          target: entity,
          new_start_time: start,
          new_end_time: end,
          reason: reason.trim() || undefined,
          notify_participants: true,
        });
      } else {
        await store.ScheduleEntity(entity.type_name, entity.id, {
          target: entity,
          start_time: start,
          end_time: end,
          notify_participants: true,
        });
      }
      setFormOpen(false);
      await load();
    } catch (e) {
      setError(errMessage(e, t('calendar.entitySchedule.saveError')));
    } finally {
      setBusy(false);
    }
  };

  const cancelSchedule = async () => {
    setBusy(true);
    setError(null);
    try {
      await store.CancelEntitySchedule(entity.type_name, entity.id, {
        target: entity,
        reason: reason.trim() || undefined,
        notify_participants: true,
      });
      setFormOpen(false);
      await load();
    } catch (e) {
      setError(errMessage(e, t('calendar.entitySchedule.cancelError')));
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return <div className="h-20 animate-pulse rounded-md bg-muted" aria-busy="true" />;
  }

  if (unavailable) {
    return (
      <p className="text-sm text-muted-foreground">{t('calendar.entitySchedule.unavailable')}</p>
    );
  }

  return (
    <div className="space-y-2">
      {error && <Alert variant="error">{error}</Alert>}

      {isScheduled && aspect ? (
        <div className="text-sm text-foreground">
          <p className="font-medium">{fmtDateTime(aspect.start_time, locale)}{aspect.end_time && ` – ${fmtTime(aspect.end_time, locale)}`}</p>
          {aspect.timezone && <p className="text-xs text-muted-foreground">{aspect.timezone}</p>}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('calendar.entitySchedule.notScheduled')}</p>
      )}

      {policy?.working_hours_enabled === false && (
        <p className="text-xs text-muted-foreground">{t('calendar.entitySchedule.workingHoursOff')}</p>
      )}

      {!formOpen && (
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="secondary" onClick={openForm}>
            {isScheduled ? t('calendar.entitySchedule.reschedule') : t('calendar.entitySchedule.schedule')}
          </Button>
          {isScheduled && (
            <Button size="sm" variant="danger" disabled={busy} onClick={cancelSchedule}>
              {t('calendar.entitySchedule.cancel')}
            </Button>
          )}
        </div>
      )}

      {formOpen && (
        <div className="space-y-2 rounded-md border border-border p-2">
          <div className="flex flex-wrap gap-2">
            <input
              type="date"
              aria-label={t('calendar.event.startDate')}
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
            />
            <input
              type="time"
              aria-label={t('calendar.event.startTime')}
              value={timeStart}
              onChange={(e) => setTimeStart(e.target.value)}
              className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
            />
            <label className="flex items-center gap-1 text-sm text-foreground">
              <input
                type="text"
                inputMode="numeric"
                aria-label={t('calendar.entitySchedule.durationMin')}
                value={durationMin === '' ? '' : String(durationMin)}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9]/g, '');
                  setDurationMin(v === '' ? '' : parseInt(v, 10));
                }}
                className="w-16 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
              />
              {t('calendar.entitySchedule.minutes')}
            </label>
          </div>
          {isScheduled && (
            <input
              type="text"
              aria-label={t('calendar.entitySchedule.reason')}
              placeholder={t('calendar.entitySchedule.reason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
            />
          )}
          {check && (
            check.available ? (
              <Alert variant="success">{t('calendar.entitySchedule.slotFree')}</Alert>
            ) : (
              <Alert variant="warning">
                {t('calendar.entitySchedule.slotBusy')}
                {(check.conflicts ?? []).slice(0, 3).map((c, i) => (
                  <span key={i} className="block text-xs">
                    {c.reason}: {fmtDateTime(c.start_time, locale)}
                  </span>
                ))}
              </Alert>
            )
          )}
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="secondary" onClick={checkAvailability} isLoading={busy}>
              {t('calendar.entitySchedule.checkAvailability')}
            </Button>
            <Button size="sm" onClick={submit} isLoading={busy}>
              {isScheduled ? t('calendar.entitySchedule.reschedule') : t('calendar.entitySchedule.schedule')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setFormOpen(false)} disabled={busy}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});
